/**
 * EnvironmentalMeasurement Edit Page
 * Edição de uma medição ambiental já registrada (#351)
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, CircularProgress } from '@mui/material';
import { PageShell } from '../../components';
import { AvisoDeFalhaAoCarregar } from '../../components/AvisoDeFalhaAoCarregar';
import { falhaDeCarga, textoDaFalhaDeCarga, type FalhaDeCarga } from '../../utils/acessoNegado';
import { environmentalMeasurementService } from '../../services/environmentalMeasurementService';
import {
  EnvironmentalMeasurement,
  EnvironmentalMeasurementFormData,
} from '../../types/environmentalMeasurement';
import { EnvironmentalMeasurementForm } from './components/EnvironmentalMeasurementForm';

export const EnvironmentalMeasurementEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [measurement, setMeasurement] = useState<EnvironmentalMeasurement | null>(null);
  const [loadingData, setLoadingData] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [falha, setFalha] = useState<FalhaDeCarga | null>(null);

  useEffect(() => {
    const loadMeasurement = async () => {
      if (!id) return;

      try {
        setLoadingData(true);
        setMeasurement(await environmentalMeasurementService.getEnvironmentalMeasurementById(id));
      } catch (err: unknown) {
        setFalha(
          falhaDeCarga(err, 'Não foi possível carregar a medição ambiental. Tente de novo.'),
        );
      } finally {
        setLoadingData(false);
      }
    };

    loadMeasurement();
  }, [id]);

  const handleSubmit = async (data: EnvironmentalMeasurementFormData) => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);
      await environmentalMeasurementService.updateEnvironmentalMeasurement(id, data);
      navigate('/environmental-measurements');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao atualizar medição ambiental');
    } finally {
      setLoading(false);
    }
  };

  const breadcrumbs = [
    { label: 'Início', path: '/' },
    { label: 'Medições Ambientais', path: '/environmental-measurements' },
    { label: 'Editar Medição' },
  ];

  if (loadingData) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title="Editar Medição Ambiental">
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  if (!measurement) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title="Editar Medição Ambiental">
        <AvisoDeFalhaAoCarregar
          mensagem={textoDaFalhaDeCarga(falha, 'Medição ambiental não encontrada.')}
          rotuloDoBotao="Voltar para medições ambientais"
          aoVoltar={() => navigate('/environmental-measurements')}
        />
      </PageShell>
    );
  }

  return (
    <PageShell
      breadcrumbs={breadcrumbs}
      title="Editar Medição Ambiental"
      subtitle={measurement.riskFactorName ?? undefined}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <EnvironmentalMeasurementForm
        initialData={measurement}
        onSubmit={handleSubmit}
        onCancel={() => navigate('/environmental-measurements')}
        loading={loading}
      />
    </PageShell>
  );
};

export default EnvironmentalMeasurementEditPage;
